const API_BASE = '/api';

// Get the base URL for media/uploads - uses relative path in production (nginx proxy)
// or localhost:3000 in development when running without Docker
export const getMediaUrl = (path: string): string => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  // In production/Docker, nginx proxies /uploads to backend
  // In dev without Docker, we need the full URL
  const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
  return `${backendUrl}${path}`;
};

interface ApiError {
  error: string;
  code?: string;
  details?: unknown;
}

interface RefreshResponse {
  accessToken: string;
}

interface CheckResponse {
  authenticated: boolean;
  accessToken?: string;
}

class ApiClient {
  private accessToken: string | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  // Check if user has a valid session via HTTP-only cookie
  async checkSession(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(`${API_BASE}/auth/check`, {
        method: 'GET',
        credentials: 'include', // Important: send cookies
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        this.accessToken = null;
        return false;
      }

      const data: CheckResponse = await response.json();

      if (data.authenticated && data.accessToken) {
        this.accessToken = data.accessToken;
        return true;
      }

      return false;
    } catch (error) {
      // Log network errors for debugging
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('Session check failed:', error.message);
      }
      return false;
    }
  }

  async refreshAccessToken(): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // Important: send cookies
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        // Refresh token invalid - clear access token
        this.accessToken = null;
        return false;
      }

      const data: RefreshResponse = await response.json();
      this.accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    }
  }

  async fetch<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include', // Important: send cookies
    });

    // Handle token expiry
    if (response.status === 401 && !isRetry) {
      const data = await response.json();
      if (data.code === 'TOKEN_EXPIRED') {
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          // Retry with new token
          return this.fetch<T>(endpoint, options, true);
        }
      }
      throw new Error(data.error || 'Unauthorized');
    }

    const data = await response.json();

    if (!response.ok) {
      const error = data as ApiError;
      throw new Error(error.error || 'Request failed');
    }

    return data as T;
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.fetch<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    // Fastify requires a body when Content-Type: application/json is set
    // Always send at least an empty object to avoid "Body cannot be empty" error
    return this.fetch<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    // Don't set Content-Type for DELETE to avoid "empty body" error
    const headers: Record<string, string> = {};

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'DELETE',
      headers,
      credentials: 'include',
    });

    if (response.status === 401) {
      const data = await response.json();
      if (data.code === 'TOKEN_EXPIRED') {
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          return this.delete<T>(endpoint);
        }
      }
      throw new Error(data.error || 'Unauthorized');
    }

    const data = await response.json();

    if (!response.ok) {
      const error = data as ApiError;
      throw new Error(error.error || 'Request failed');
    }

    return data as T;
  }

  // Fetch a blob (for PDF downloads, etc.)
  async fetchBlob(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<{ blob: Blob; filename: string }> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    // Handle token expiry
    if (response.status === 401 && !isRetry) {
      const data = await response.json();
      if (data.code === 'TOKEN_EXPIRED') {
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          return this.fetchBlob(endpoint, options, true);
        }
      }
      throw new Error(data.error || 'Unauthorized');
    }

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Request failed');
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition?.split('filename="')[1]?.replace('"', '') || 'download';

    return { blob, filename };
  }

  async postBlob(endpoint: string, body?: unknown): Promise<{ blob: Blob; filename: string }> {
    return this.fetchBlob(endpoint, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    });
  }
}

export const api = new ApiClient();
